import { useCallback, useEffect, useState } from "react";
import { formatDate } from "@aigov/domain";
import { api } from "../api";
import { ErrorNote, Pill, errMsg } from "../ui";

interface Signal {
  id: string; sourceId: string | null; kind: "page_changed" | "new_item" | "fetch_failed" | "blocked" | "review_due";
  title: string; url: string; detail: string; obligations: string[]; frameworks: string[]; detectedAt: string;
  status: "new" | "reviewing" | "actioned" | "dismissed"; note: string; resolvedInVersion: string;
}
interface SourceRow { id: string; jurisdiction: string; title: string; url: string; kind: string; lastCheckedAt: string | null; lastStatus: string; failures: number }
interface ReviewRow { id: string; jurisdiction: string; title: string; confidence: "S" | "M"; reviewedOn: string; dueOn: string; overdue: boolean; sources: string[] }
interface WatchDto { libraryVersion: string; sources: SourceRow[]; reviews: ReviewRow[]; signals: Signal[] }

const KIND: Record<Signal["kind"], [string, "crit" | "warn" | "info" | "mute"]> = {
  page_changed: ["Page changed", "warn"], new_item: ["New publication", "info"], review_due: ["Review due", "warn"],
  fetch_failed: ["Source unreachable", "crit"], blocked: ["Manual check needed", "mute"],
};
const OPEN = new Set(["new", "reviewing"]);

export function WatchView({ toast }: { toast: (m: string) => void }) {
  const [data, setData] = useState<WatchDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [show, setShow] = useState<"open" | "all">("open");
  const [running, setRunning] = useState(false);
  const [allReviews, setAllReviews] = useState(false);

  const load = useCallback(() => api<WatchDto>("GET", "/watch").then(setData).catch((e) => setError(errMsg(e))), []);
  useEffect(() => { void load(); }, [load]);
  if (!data) return error ? <ErrorNote error={error} /> : <p className="muted">Loading the regulatory watch…</p>;

  const open = data.signals.filter((s) => OPEN.has(s.status));
  const in30 = new Date(Date.now() + 30 * 864e5).toISOString().slice(0, 10);
  const overdue = data.reviews.filter((r) => r.overdue);
  const dueSoon = data.reviews.filter((r) => !r.overdue && r.dueOn <= in30);
  const attention = data.sources.filter((s) => s.failures > 0 || !s.lastCheckedAt || s.lastStatus.startsWith("blocked"));
  const list = show === "open" ? open : data.signals;
  const reviews = allReviews ? data.reviews : data.reviews.filter((r) => r.dueOn <= in30);

  return (
    <div className="stack">
      <section className="panel">
        <div className="row spread">
          <div><h2>Regulatory watch</h2>
            <p className="muted small" style={{ margin: "4px 0 0", maxWidth: "75ch" }}>
              Official sources are checked automatically and every obligation has a review date. Nothing here changes the library by itself:
              you decide what each signal means, then ship the change as a new library version (currently v{data.libraryVersion}).</p></div>
          <button className="btn primary" disabled={running} onClick={async () => {
            setRunning(true);
            try {
              const s = await api<{ checked: number; changed: number; newItems: number; failed: number; signals: number }>("POST", "/watch/run");
              toast(`Checked ${s.checked} sources: ${s.signals} new signal${s.signals === 1 ? "" : "s"}`);
              await load();
            } catch (e) { toast(errMsg(e)); } finally { setRunning(false); }
          }}>{running ? "Checking sources…" : "Check sources now"}</button>
        </div>
      </section>

      <div className="grid3">
        <div className="panel stat"><h3>Open signals</h3><b>{open.length}</b><span className="muted small">{open.filter((s) => s.status === "new").length} not yet looked at</span></div>
        <div className="panel stat"><h3>Reviews overdue</h3><b>{overdue.length}</b><span className="muted small">{dueSoon.length} more due in the next 30 days</span></div>
        <div className="panel stat"><h3>Sources needing attention</h3><b>{attention.length}</b><span className="muted small">of {data.sources.length} watched sources</span></div>
      </div>

      <section className="panel">
        <div className="row spread" style={{ marginBottom: 6 }}>
          <h2>Signals</h2>
          <div className="row small">
            <label><input type="radio" name="sig" checked={show === "open"} onChange={() => setShow("open")} /> Open</label>
            <label><input type="radio" name="sig" checked={show === "all"} onChange={() => setShow("all")} /> All</label>
          </div>
        </div>
        {list.length === 0 ? <p className="muted">{show === "open" ? "Nothing waiting for review." : "No signals yet. Run a check to set the baseline."}</p>
          : list.map((s) => <SignalRow key={`${s.id}-${s.status}`} s={s} version={data.libraryVersion} onSaved={async () => { toast("Saved"); await load(); }} />)}
      </section>

      <section className="panel">
        <div className="row spread"><h2>Review schedule</h2>
          <button className="btn ghost small" onClick={() => setAllReviews((v) => !v)}>{allReviews ? "Show due in 30 days" : `Show all ${data.reviews.length}`}</button></div>
        <p className="muted small" style={{ margin: "4px 0 8px" }}>Items marked "verify" and watch items are reviewed monthly, dated laws quarterly, voluntary guidance every six months. Record a review by updating its date in the library.</p>
        {reviews.length === 0 ? <p className="muted">No reviews due in the next 30 days.</p> : (
          <div className="scroll"><table>
            <thead><tr><th>Obligation</th><th>Last reviewed</th><th>Due</th><th>Automated source</th></tr></thead>
            <tbody>{reviews.map((r) => (
              <tr key={r.id}><td><span className="jur">{r.jurisdiction}</span> {r.title}{r.confidence === "M" && <span className="muted small"> · <span className="vdot v-M" />verify</span>}</td>
                <td className="small">{formatDate(r.reviewedOn)}</td>
                <td>{r.overdue ? <Pill tone="crit">Overdue {formatDate(r.dueOn)}</Pill> : r.dueOn <= in30 ? <Pill tone="warn">{formatDate(r.dueOn)}</Pill> : <span className="small">{formatDate(r.dueOn)}</span>}</td>
                <td className="small mono">{r.sources.join(", ") || <span className="muted">none: check by hand</span>}</td></tr>))}
            </tbody></table></div>)}
      </section>

      <section className="panel">
        <h2 style={{ marginBottom: 6 }}>Watched sources</h2>
        <div className="scroll"><table>
          <thead><tr><th>Source</th><th>Type</th><th>Last checked</th><th>Status</th></tr></thead>
          <tbody>{data.sources.map((s) => (
            <tr key={s.id}><td><span className="jur">{s.jurisdiction}</span> <a href={s.url} target="_blank" rel="noopener noreferrer">{s.title}</a></td>
              <td className="small">{s.kind === "feed" ? "Feed" : "Page"}</td>
              <td className="small">{s.lastCheckedAt ? new Date(s.lastCheckedAt).toLocaleString("en-GB") : "Not yet"}</td>
              <td>{s.failures > 0 ? <Pill tone="crit">{s.lastStatus} ({s.failures}×)</Pill>
                : s.lastStatus.startsWith("blocked") ? <Pill tone="mute">Blocked by robots.txt</Pill>
                : s.lastCheckedAt ? <Pill tone="ok">{s.lastStatus}</Pill> : <Pill tone="mute">Not checked</Pill>}</td></tr>))}
          </tbody></table></div>
        <p className="muted small" style={{ marginBottom: 0 }}>Checked daily by the watch service. Add or change sources in packages/domain/src/watch.ts.</p>
      </section>
    </div>
  );
}

function SignalRow({ s, version, onSaved }: { s: Signal; version: string; onSaved: () => Promise<void> }) {
  const [status, setStatus] = useState(s.status);
  const [note, setNote] = useState(s.note);
  const [ver, setVer] = useState(s.resolvedInVersion);
  const [error, setError] = useState<string | null>(null);
  const [k, tone] = KIND[s.kind];
  return (
    <div className="ob" style={{ gridTemplateColumns: "minmax(0,1fr) auto" }}>
      <div style={{ display: "grid", gap: 4 }}>
        <div className="row" style={{ gap: 8 }}><Pill tone={tone}>{k}</Pill><span className="t">{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer">{s.title}</a> : s.title}</span></div>
        <div className="muted small">Detected {new Date(s.detectedAt).toLocaleDateString("en-GB")}
          {s.obligations.length > 0 && <> · Affects {s.obligations.map((o) => <span key={o} className="chip">{o}</span>)}</>}
          {s.frameworks.length > 0 && <> · Framework {s.frameworks.map((f) => <span key={f} className="chip">{f}</span>)}</>}</div>
        {s.detail && <details><summary className="small">What changed</summary><pre className="mono" style={{ whiteSpace: "pre-wrap", margin: "6px 0 0" }}>{s.detail}</pre></details>}
        <div className="row" style={{ alignItems: "flex-start" }}>
          <select className="input" style={{ width: "auto" }} aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as Signal["status"])}>
            <option value="new">New</option><option value="reviewing">Reviewing</option><option value="actioned">Library updated</option><option value="dismissed">No change needed</option>
          </select>
          <input className="input" style={{ maxWidth: 360 }} aria-label="Decision note" placeholder="Decision and reasoning" value={note} onChange={(e) => setNote(e.target.value)} />
          {status === "actioned" && <input className="input" style={{ width: 110 }} aria-label="Library version" placeholder={`e.g. ${version}`} value={ver} onChange={(e) => setVer(e.target.value)} />}
          <button className="btn" onClick={async () => {
            setError(null);
            try { await api("PATCH", `/watch/signals/${s.id}`, { status, note, resolvedInVersion: status === "actioned" ? ver : "" }); await onSaved(); }
            catch (e) { setError(errMsg(e)); }
          }}>Save</button>
        </div>
        <ErrorNote error={error} />
      </div>
    </div>
  );
}
